import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { RouterClient } from "@orpc/server";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import type { AppRouter } from "@alm/api-contracts";
import { apiHttpOrigin } from "@/lib/env";
import { noteServerDate } from "@/lib/messaging/live/server-clock";
import { tapEventStream, type HeardContext } from "@/lib/query/stream-tap";

// Every answer's Date keeps the server clock fresh, so a laptop that slept or drifted is caught.
const link = new RPCLink({
  url: `${apiHttpOrigin}/rpc`,
  fetch: async (request, init) => {
    const sentAt = Date.now();
    const response = await globalThis.fetch(request, { ...init, credentials: "include" });
    noteServerDate(response.headers.get("date"), sentAt, Date.now());
    return response;
  },
});

export const orpcClient: RouterClient<AppRouter> = createORPCClient(link);
export const orpc = createTanstackQueryUtils(orpcClient);

// The live stream's own line: a call's `heard` hears every chunk, so a silent line shows.
const eventsLink = new RPCLink<HeardContext>({
  url: `${apiHttpOrigin}/rpc`,
  fetch: async (request, init, options) => {
    const sentAt = Date.now();
    const response = await globalThis.fetch(request, { ...init, credentials: "include" });
    noteServerDate(response.headers.get("date"), sentAt, Date.now());
    return tapEventStream(response, options.context.heard);
  },
});

export const orpcEventsClient: RouterClient<AppRouter, HeardContext> = createORPCClient(eventsLink);

export type Output<T extends (...args: never[]) => Promise<unknown>> = Awaited<ReturnType<T>>;

// Public pages (careers, jobs, signing links, marketing forms) send no cookie: their hostnames
// cannot be enumerated so the API cannot echo the origin with credentials, and what they read is
// public or authorized by a token in the URL.
const publicLink = new RPCLink({
  url: `${apiHttpOrigin}/rpc`,
  fetch: (request, init) => globalThis.fetch(request, { ...init, credentials: "omit" }),
});

export const orpcPublicClient: RouterClient<AppRouter> = createORPCClient(publicLink);
export const orpcPublic = createTanstackQueryUtils(orpcPublicClient);
